import { useEffect, useState, useCallback, useRef } from "react";
import api, { API, getToken, fileUrl, eur2, formatApiErrorDetail } from "@/lib/api";
import { STATUS_STYLES, CATEGORY_ICONS } from "@/lib/constants";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  Filter, Download, Loader2, Phone, Mail, MapPin, Calendar, User, Euro,
  History, StickyNote, Save, Inbox, FileText, Star, UploadCloud, FileCheck2, Send,
} from "lucide-react";

const STATUSES = ["Nouvelle", "Artisan assigné", "Devis envoyé", "Chantier accepté", "Terminé", "Annulé"];
const CATEGORIES = ["Plomberie", "Électricité", "Carrelage", "Peinture", "Maçonnerie", "Menuiserie", "Rénovation salle de bain", "Rénovation intérieure", "Autre"];

function StatusBadge({ status }) {
  return <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${STATUS_STYLES[status] || ""}`}>{status}</span>;
}

export default function Requests() {
  const [requests, setRequests] = useState([]);
  const [artisans, setArtisans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (categoryFilter !== "all") params.category = categoryFilter;
      const { data } = await api.get("/requests", { params });
      setRequests(data);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, categoryFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/artisans").then((r) => setArtisans(r.data)).catch(() => {}); }, []);

  const exportCsv = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (categoryFilter !== "all") params.set("category", categoryFilter);
      const res = await fetch(`${API}/requests-export/csv?${params.toString()}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error("Erreur export");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `demandes_rbhabitat_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export CSV téléchargé");
    } catch (e) {
      toast.error("Impossible d'exporter le CSV");
    }
  };

  const onUpdated = (updated) => {
    setSelected(updated);
    setRequests((rs) => rs.map((r) => (r.id === updated.id ? updated : r)));
    load();
  };

  return (
    <div className="p-5 sm:p-8 max-w-7xl mx-auto" data-testid="requests-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Demandes de travaux</h1>
          <p className="text-slate-500 mt-1">{requests.length} demande(s) affichée(s)</p>
        </div>
        <Button onClick={exportCsv} variant="outline" className="border-slate-300" data-testid="export-csv-button">
          <Download className="h-4 w-4 mr-2" /> Exporter en CSV
        </Button>
      </div>

      {/* Filters */}
      <div className="mt-6 flex flex-wrap items-center gap-3 bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex items-center gap-2 text-slate-500 text-sm font-medium"><Filter className="h-4 w-4" /> Filtres</div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]" data-testid="filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-[200px]" data-testid="filter-category"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les catégories</SelectItem>
            {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* List */}
      {loading ? (
        <div className="grid place-items-center py-32"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>
      ) : requests.length === 0 ? (
        <div className="mt-10 text-center py-20 bg-white rounded-2xl border border-slate-200">
          <Inbox className="h-10 w-10 mx-auto text-slate-300" />
          <p className="mt-3 text-slate-500">Aucune demande pour ces filtres.</p>
        </div>
      ) : (
        <div className="mt-6 space-y-3" data-testid="requests-list">
          {requests.map((r) => {
            const Icon = CATEGORY_ICONS[r.category] || Inbox;
            return (
              <button
                key={r.id}
                onClick={() => setSelected(r)}
                data-testid={`request-row-${r.reference}`}
                className="w-full text-left bg-white rounded-xl border border-slate-200 p-4 sm:p-5 hover:border-blue-300 hover:shadow-md transition-all flex items-center gap-4"
              >
                <div className="h-11 w-11 rounded-xl bg-blue-50 grid place-items-center shrink-0">
                  <Icon className="h-5 w-5 text-blue-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono-nums text-xs font-bold text-slate-400">{r.reference}</span>
                    <StatusBadge status={r.status} />
                  </div>
                  <p className="font-display font-semibold text-slate-900 mt-1 truncate">{r.category} · {r.city}</p>
                  <p className="text-sm text-slate-500 truncate">{r.full_name} · {r.phone}</p>
                </div>
                <div className="hidden sm:block text-right shrink-0">
                  {r.assigned_artisan ? (
                    <p className="text-sm font-medium text-slate-700">{r.assigned_artisan.name}</p>
                  ) : (
                    <p className="text-sm text-slate-400 italic">Non assigné</p>
                  )}
                  {r.commission ? <p className="font-mono-nums text-sm font-bold text-emerald-600 mt-0.5">+{eur2(r.commission)}</p> : null}
                </div>
              </button>
            );
          })}
        </div>
      )}

      <RequestDetail request={selected} artisans={artisans} onClose={() => setSelected(null)} onUpdated={onUpdated} />
    </div>
  );
}

function RequestDetail({ request, artisans, onClose, onUpdated }) {
  const [form, setForm] = useState({ status: "", assigned_artisan_id: "", amount_ht: "", internal_notes: "", connection_date: "" });
  const [saving, setSaving] = useState(false);
  const [uploadingQuote, setUploadingQuote] = useState(false);
  const quoteInput = useRef(null);

  useEffect(() => {
    if (request) {
      setForm({
        status: request.status,
        assigned_artisan_id: request.assigned_artisan_id || "none",
        amount_ht: request.amount_ht ?? "",
        internal_notes: request.internal_notes || "",
        connection_date: request.connection_date ? request.connection_date.slice(0, 10) : "",
      });
    }
  }, [request]);

  if (!request) return null;

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        status: form.status,
        assigned_artisan_id: form.assigned_artisan_id === "none" ? "" : form.assigned_artisan_id,
        internal_notes: form.internal_notes,
        amount_ht: form.amount_ht === "" ? null : parseFloat(form.amount_ht),
        connection_date: form.connection_date || null,
      };
      const { data } = await api.patch(`/requests/${request.id}`, payload);
      toast.success("Demande mise à jour");
      onUpdated(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const commissionPreview = form.status === "Terminé" && form.amount_ht ? parseFloat(form.amount_ht) * 0.1 : null;

  const uploadQuote = async (fileList) => {
    const file = fileList?.[0];
    if (!file) return;
    if (!request.assigned_artisan_id) {
      toast.error("Assignez d'abord un artisan, puis enregistrez.");
      return;
    }
    setUploadingQuote(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/requests/${request.id}/quote`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Devis envoyé au client par email");
      onUpdated(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setUploadingQuote(false);
    }
  };

  const downloadInvoice = async () => {
    try {
      const res = await fetch(`${API}/requests/${request.id}/invoice`, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `facture_commission_${request.reference}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Facture téléchargée");
    } catch (e) {
      toast.error("Facture indisponible");
    }
  };

  return (
    <Sheet open={!!request} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto p-0" data-testid="request-detail-panel">
        <SheetHeader className="p-6 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center gap-2">
            <span className="font-mono-nums text-xs font-bold text-slate-400">{request.reference}</span>
            <StatusBadge status={request.status} />
          </div>
          <SheetTitle className="font-display text-xl text-left">{request.category} · {request.city}</SheetTitle>
        </SheetHeader>

        <div className="p-6 space-y-6">
          {/* Client info */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-slate-700"><User className="h-4 w-4 text-slate-400" /> {request.full_name}</div>
            <div className="flex items-center gap-2 text-sm text-slate-700"><Phone className="h-4 w-4 text-slate-400" /> {request.phone}</div>
            <div className="flex items-center gap-2 text-sm text-slate-700"><Mail className="h-4 w-4 text-slate-400" /> {request.email}</div>
            <div className="flex items-center gap-2 text-sm text-slate-700"><MapPin className="h-4 w-4 text-slate-400" /> {request.city}</div>
          </div>

          <div className="rounded-lg bg-slate-50 border border-slate-100 p-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Description</p>
            <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{request.description}</p>
          </div>

          {/* Photos */}
          {request.photos?.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Photos du chantier</p>
              <div className="flex flex-wrap gap-2">
                {request.photos.map((p) => (
                  <a key={p.path} href={fileUrl(p.path)} target="_blank" rel="noreferrer" className="h-20 w-20 rounded-lg overflow-hidden border border-slate-200">
                    <img src={fileUrl(p.path)} alt={p.filename} className="h-full w-full object-cover" />
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Management */}
          <div className="space-y-4 rounded-xl border border-slate-200 p-4">
            <p className="font-display font-semibold text-slate-900">Gestion du dossier</p>

            <div>
              <Label className="text-slate-700 text-sm">Statut</Label>
              <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                <SelectTrigger className="mt-1.5" data-testid="detail-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-slate-700 text-sm">Artisan assigné</Label>
              <Select value={form.assigned_artisan_id} onValueChange={(v) => setForm((f) => ({ ...f, assigned_artisan_id: v }))}>
                <SelectTrigger className="mt-1.5" data-testid="detail-artisan-select"><SelectValue placeholder="Sélectionner un artisan" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucun</SelectItem>
                  {artisans.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} — {a.city}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-slate-700 text-sm flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> Date de mise en relation</Label>
              <Input type="date" className="mt-1.5" value={form.connection_date} onChange={(e) => setForm((f) => ({ ...f, connection_date: e.target.value }))} data-testid="detail-connection-date" />
            </div>

            <div>
              <Label className="text-slate-700 text-sm flex items-center gap-1"><Euro className="h-3.5 w-3.5" /> Montant du chantier HT (€)</Label>
              <Input type="number" step="0.01" className="mt-1.5" placeholder="0.00" value={form.amount_ht} onChange={(e) => setForm((f) => ({ ...f, amount_ht: e.target.value }))} data-testid="detail-amount-input" />
              {commissionPreview != null && (
                <p className="mt-2 text-sm rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 px-3 py-2 font-medium" data-testid="commission-preview">
                  Commission 10% : <span className="font-mono-nums font-bold">{eur2(commissionPreview)}</span>
                </p>
              )}
              {request.commission ? (
                <p className="mt-2 text-sm text-slate-600">Commission enregistrée : <span className="font-mono-nums font-bold text-emerald-600">{eur2(request.commission)}</span></p>
              ) : null}
            </div>

            <div>
              <Label className="text-slate-700 text-sm flex items-center gap-1"><StickyNote className="h-3.5 w-3.5" /> Notes internes</Label>
              <Textarea className="mt-1.5 min-h-[80px]" placeholder="Remarques réservées à l'équipe..." value={form.internal_notes} onChange={(e) => setForm((f) => ({ ...f, internal_notes: e.target.value }))} data-testid="internal-notes-input" />
            </div>

            <Button onClick={save} disabled={saving} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="detail-save-button">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-2" /> Enregistrer</>}
            </Button>
          </div>

          {/* Devis PDF */}
          <div className="rounded-xl border border-slate-200 p-4" data-testid="quote-section">
            <p className="font-display font-semibold text-slate-900 flex items-center gap-2"><FileText className="h-4 w-4 text-slate-400" /> Devis (PDF)</p>
            {request.quote_pdf ? (
              <div className="mt-3">
                <div className="flex items-center gap-3 rounded-lg bg-emerald-50 border border-emerald-200 p-3" data-testid="quote-uploaded-block">
                  <FileCheck2 className="h-5 w-5 text-emerald-600 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-emerald-900 truncate">{request.quote_pdf.filename}</p>
                    <p className="text-xs text-emerald-700">Transmis au client le {new Date(request.quote_pdf.uploaded_at).toLocaleString("fr-FR")}</p>
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  <a href={fileUrl(request.quote_pdf.path)} target="_blank" rel="noreferrer" data-testid="quote-view-link">
                    <Button variant="outline" size="sm" className="border-slate-300"><Download className="h-3.5 w-3.5 mr-1.5" /> Consulter le devis</Button>
                  </a>
                  <Button variant="ghost" size="sm" onClick={() => quoteInput.current?.click()} disabled={uploadingQuote} data-testid="quote-replace-button">
                    {uploadingQuote ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><UploadCloud className="h-3.5 w-3.5 mr-1.5" /> Remplacer & renvoyer</>}
                  </Button>
                </div>
              </div>
            ) : request.assigned_artisan_id ? (
              <div className="mt-3">
                <p className="text-sm text-slate-500 mb-2">Téléversez le devis PDF : la demande passera automatiquement en « Devis envoyé » et le client recevra le devis par email.</p>
                <div
                  onClick={() => quoteInput.current?.click()}
                  className="cursor-pointer rounded-xl border-2 border-dashed border-slate-300 hover:border-blue-400 hover:bg-blue-50/50 transition-colors p-5 text-center"
                  data-testid="quote-upload-dropzone"
                >
                  {uploadingQuote ? <Loader2 className="h-6 w-6 mx-auto animate-spin text-blue-600" /> : <Send className="h-6 w-6 mx-auto text-slate-400" />}
                  <p className="text-sm text-slate-500 mt-2">Cliquez pour sélectionner le devis (PDF)</p>
                </div>
              </div>
            ) : (
              <p className="mt-3 text-sm text-slate-400 italic">Assignez un artisan puis enregistrez pour pouvoir téléverser le devis.</p>
            )}
            <input ref={quoteInput} type="file" accept="application/pdf,.pdf" className="hidden" onChange={(e) => { uploadQuote(e.target.files); e.target.value = ""; }} data-testid="quote-file-input" />
          </div>

          {/* Invoice */}
          {request.commission ? (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-testid="commission-invoice-block">
              <p className="font-display font-semibold text-emerald-900 flex items-center gap-2"><FileText className="h-4 w-4" /> Facture de commission</p>
              <p className="text-sm text-emerald-700 mt-1">Commission 10% : <span className="font-mono-nums font-bold">{eur2(request.commission)}</span></p>
              <Button onClick={downloadInvoice} variant="outline" className="mt-3 border-emerald-300 text-emerald-800 hover:bg-emerald-100" data-testid="download-invoice-button">
                <Download className="h-4 w-4 mr-2" /> Télécharger la facture PDF
              </Button>
            </div>
          ) : null}

          {/* Client rating */}
          {request.rating ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="request-rating-block">
              <p className="font-display font-semibold text-amber-900 flex items-center gap-2"><Star className="h-4 w-4" /> Note du client</p>
              <div className="flex items-center gap-1 mt-2">
                {[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`h-5 w-5 ${n <= request.rating ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />)}
                <span className="ml-1 font-mono-nums font-bold text-amber-900">{request.rating}/5</span>
              </div>
              {request.rating_comment && <p className="mt-2 text-sm text-amber-800 italic">« {request.rating_comment} »</p>}
            </div>
          ) : null}

          {/* History */}
          <div data-testid="timestamped-history-log">
            <p className="font-display font-semibold text-slate-900 flex items-center gap-2 mb-3"><History className="h-4 w-4 text-slate-400" /> Historique horodaté</p>
            <div className="space-y-3">
              {[...(request.history || [])].reverse().map((h, i) => (
                <div key={i} className="flex gap-3">
                  <div className="mt-1.5 h-2 w-2 rounded-full bg-blue-500 shrink-0" />
                  <div>
                    <p className="text-sm text-slate-700">{h.note}</p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {new Date(h.timestamp).toLocaleString("fr-FR")} · par {h.changed_by}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
